/**
 * Maps GET /profile and GET /users/:id envelopes into the shape the existing
 * Ionic screens already render. New API fields (isPremium) are aliased to
 * legacy names (subscribed) so UI can migrate gradually.
 */

export type ProfileEnvelope = {
    missingFields?: string[];
    userData?: Record<string, any>;
    photos?: any;
    welcomeBack?: any;
};

function relationFlag(relationStatus: any, key: string): boolean {
    if (!relationStatus || typeof relationStatus !== 'object') return false;
    return Boolean(relationStatus[key] ?? relationStatus[key.replace(/([A-Z])/g, '_$1').toLowerCase()]);
}

function unwrapCatalogRows(rows: any[], nestedKey: string): any[] {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => {
        if (row && typeof row === 'object' && row[nestedKey]) {
            return row[nestedKey];
        }
        return row;
    }).filter(Boolean);
}

function locationFrom(userData: Record<string, any>) {
    const loc = userData.location;
    if (loc && typeof loc === 'object') {
        const name = [loc.city, loc.state, loc.country].filter(Boolean).join(', ')
            || loc.address
            || loc.name
            || '';
        return {
            name,
            lat: Number(loc.lat ?? 0),
            lng: Number(loc.lng ?? 0),
            address: loc.address,
            city: loc.city,
            state: loc.state,
            country: loc.country,
            zip: loc.zip,
        };
    }
    const name = [userData.city, userData.state, userData.country].filter(Boolean).join(', ');
    if (!name && userData.lat == null) return null;
    return {
        name: name || userData.address || '',
        lat: Number(userData.lat ?? 0),
        lng: Number(userData.lng ?? 0),
    };
}

function buildOnboarding(flat: Record<string, any>, missingFields: string[] = []) {
    const missing = new Set((missingFields || []).map((f) => String(f).toLowerCase()));
    const interests = flat.sexual_interests || flat.sexualInterests || [];
    const desires = flat.desires || [];

    return {
        email_verified: Boolean(flat.emailVerified ?? flat.email_verified ?? flat.email),
        password_set: true,
        name_set: !missing.has('name') && Boolean(flat.name),
        dob_set: !missing.has('dob') && Boolean(flat.dob),
        bio_set: !missing.has('bio') && Boolean(flat.bio),
        gender_set: !missing.has('gender') && Boolean(flat.genderId || flat.gender),
        sexuality_set: Boolean(flat.sexualityId || flat.sexuality),
        sexual_interests_set: !missing.has('sexualinterests') && !missing.has('sexual_interests')
            && Array.isArray(interests) && interests.length > 0,
        desires_set: !missing.has('desires') && Array.isArray(desires) && desires.length > 0,
        avatar_set: Boolean(flat.avatar),
    };
}

/** True when the profile payload itself says onboarding is done. */
export function profileLooksComplete(user: any): boolean {
    if (!user) return false;
    const missing = user.missingFields;
    if (Array.isArray(missing) && missing.length === 0) {
        return Boolean(user.name && user.onboarding?.name_set !== false);
    }
    const ob = user.onboarding;
    if (!ob) return false;
    return Boolean(
        ob.email_verified
        && ob.password_set
        && ob.name_set
        && ob.bio_set
        && ob.desires_set
        && ob.sexual_interests_set
    );
}

export function isTruthyPreference(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true' || value === 'TRUE';
}

/**
 * Normalize a profile envelope (or a bare userData object) into UserInterface-like shape.
 */
export function normalizeProfile(payload: ProfileEnvelope | Record<string, any> | null | undefined, preferences: Record<string, any> = {}): any {
    if (!payload) return null;

    const isEnvelope = 'userData' in (payload as any) || 'missingFields' in (payload as any) || 'photos' in (payload as any);
    const envelope = isEnvelope
        ? (payload as ProfileEnvelope)
        : {userData: payload as Record<string, any>, missingFields: [], photos: undefined};

    const userData = {...(envelope.userData || {})};
    const nestedProfile = userData.profile && typeof userData.profile === 'object' ? userData.profile : {};
    const missingFields = envelope.missingFields || [];
    const photosPayload = envelope.photos ?? userData.photos ?? null;

    const photosList = Array.isArray(photosPayload)
        ? photosPayload
        : (Array.isArray(photosPayload?.gallery) ? photosPayload.gallery : []);

    const profilePhotoUrl = (!Array.isArray(photosPayload) && photosPayload?.profilePhoto)
        || userData.profilePhoto
        || userData.profile_photo
        || userData.avatar
        || userData.avatarUrl
        || null;

    const coverPhotoUrl = (!Array.isArray(photosPayload) && photosPayload?.coverPhoto)
        || userData.coverPhoto
        || userData.cover_photo
        || userData.coverUrl
        || null;

    const isPremium = Boolean(userData.isPremium ?? userData.is_premium ?? userData.subscribed);
    const idVerified = Boolean(
        userData.isVerified
        ?? userData.id_verified
        ?? userData.idVerified
        ?? userData.verified
        ?? (String(userData.verificationStatus || '').toUpperCase() === 'APPROVED')
    );

    const name = nestedProfile.name
        || userData.name
        || userData.userName
        || userData.user_name
        || '';

    const gender = nestedProfile.gender || userData.gender || null;
    const sexuality = nestedProfile.sexuality || userData.sexuality || null;
    const pronouns = nestedProfile.pronoun || nestedProfile.pronouns || userData.pronouns || null;
    const bodyType = nestedProfile.bodyType || nestedProfile.body_type || userData.bodyType || userData.body_type || null;
    const smokingType = nestedProfile.smokingType || nestedProfile.smoking_type || userData.smokingType || null;
    const drinkingType = nestedProfile.drinkingType || nestedProfile.drinking_type || userData.drinkingType || null;
    const religion = nestedProfile.religion || userData.religion || null;
    const dob = nestedProfile.dob || userData.dob || null;
    const heightInCm = nestedProfile.heightInCm ?? userData.heightInCm ?? null;

    const desires = unwrapCatalogRows(userData.desires || [], 'desire');
    const sexualInterests = unwrapCatalogRows(userData.sexualInterests || userData.sexual_interests || [], 'sexuality');

    const counts = userData.counts || {};
    const relationStatus = userData.relationStatus || userData.relation_status || {};

    const flatForOnboarding = {
        name,
        bio: userData.bio || '',
        dob,
        gender,
        genderId: nestedProfile.genderId || userData.genderId,
        sexuality,
        sexualityId: nestedProfile.sexualityId || userData.sexualityId,
        desires,
        sexual_interests: sexualInterests,
        avatar: profilePhotoUrl,
        email: userData.email,
        emailVerified: true,
    };

    const onboarding = buildOnboarding(flatForOnboarding, missingFields);
    const mergedPreferences = {
        ...(typeof preferences === 'object' && preferences ? preferences : {}),
        ...(userData.preferences || {}),
    };

    // Existing accounts often have no ONBOARDING_COMPLETE pref on the new API.
    if (missingFields.length === 0 && onboarding.name_set && !('ONBOARDING_COMPLETE' in mergedPreferences)) {
        mergedPreferences.ONBOARDING_COMPLETE = true;
        if (userData.accountType || userData.account_type) {
            mergedPreferences.ONBOARDING_ACCOUNT_TYPE = userData.accountType || userData.account_type;
        }
    }

    const primaryProfile = {
        id: nestedProfile.id || userData.id,
        name,
        gender,
        dob,
        sexuality,
        pronouns,
        body_type: bodyType,
        smoking_type: smokingType,
        drinking_type: drinkingType,
        religion,
        height: heightInCm != null ? {value: heightInCm} : (userData.height || null),
        has_piercings: nestedProfile.hasPiercings ?? nestedProfile.has_piercings ?? userData.hasPiercings ?? null,
        has_tattoos: nestedProfile.hasTattoos ?? nestedProfile.has_tattoos ?? userData.hasTattoos ?? null,
    };

    return {
        ...userData,
        id: Number(userData.id),
        email: userData.email ?? '',
        slug: userData.slug || userData.userName || userData.user_name || String(userData.id),
        name,
        userName: userData.userName || userData.user_name,
        bio: userData.bio || '',
        avatar: profilePhotoUrl,
        cover_photo: coverPhotoUrl,
        avatar_thumbnail: userData.avatar_thumbnail || userData.avatarThumbnail || profilePhotoUrl,
        subscribed: isPremium,
        isPremium,
        ambassador: Boolean(userData.ambassador ?? userData.isAmbassador),
        beta_tester: Boolean(userData.beta_tester ?? userData.betaTester ?? userData.isBetaTester),
        app_reviewer: Boolean(userData.app_reviewer ?? userData.appReviewer),
        id_verified: idVerified,
        hasPrivacyPin: Boolean(userData.hasPrivacyPin ?? userData.has_privacy_pin),
        hasPendingVerificationVideo: Boolean(userData.hasPendingVerificationVideo),
        allowedNsfwLevels: userData.allowedNsfwLevels || [],
        blurMatureByDefault: Boolean(userData.blurMatureByDefault),
        showPremiumFeatureBadges: Boolean(userData.showPremiumFeatureBadges),
        relationship_status: userData.relationship_status || userData.relationshipStatus || userData.accountType,
        account_type: userData.account_type || userData.accountType,
        location: locationFrom(userData),
        lat: Number(userData.location?.lat ?? userData.lat ?? 0) || undefined,
        lng: Number(userData.location?.lng ?? userData.lng ?? 0) || undefined,
        available_to_meet_until: userData.availableToMeetUntil ?? userData.available_to_meet_until ?? null,
        last_active: userData.last_active || userData.lastActive || null,
        is_friend: relationFlag(relationStatus, 'isFriend') || relationFlag(relationStatus, 'is_friend') || Boolean(userData.is_friend),
        is_following: relationFlag(relationStatus, 'isFollowing') || relationFlag(relationStatus, 'is_following') || Boolean(userData.is_following),
        is_friend_requested: relationFlag(relationStatus, 'isFriendRequested')
            || relationFlag(relationStatus, 'is_friend_requested')
            || Boolean(relationStatus.friendStatus === 'pending' || relationStatus.friendRequestId)
            || Boolean(userData.is_friend_requested),
        has_reviewed: Boolean(userData.has_reviewed ?? userData.hasReviewed),
        canDirectMessage: Boolean(userData.canDirectMessage ?? true),
        gender,
        dob,
        height: primaryProfile.height,
        pronouns,
        body_type: bodyType,
        smoking_type: smokingType,
        drinking_type: drinkingType,
        religion,
        sexuality,
        has_piercings: primaryProfile.has_piercings,
        has_tattoos: primaryProfile.has_tattoos,
        desires,
        sexual_interests: sexualInterests,
        profiles: Array.isArray(userData.profiles) && userData.profiles.length
            ? userData.profiles
            : [primaryProfile],
        reviews: counts.reviews ?? userData.reviews ?? (userData.userReviews?.length ?? 0),
        friends: counts.friends ?? userData.totalFriendsCount ?? userData.friends ?? 0,
        friend_requests: counts.friend_requests ?? counts.friendRequests ?? userData.friend_requests ?? 0,
        likes: counts.likes ?? userData.likes ?? 0,
        following: counts.followings ?? counts.following ?? userData.following ?? 0,
        followers: counts.followers ?? userData.followers ?? 0,
        winks: counts.winksReceived ?? counts.winks ?? userData.winks ?? 0,
        posts: counts.posts ?? userData.posts ?? 0,
        gallery: counts.photos ?? counts.gallery ?? userData.gallery ?? photosList.length ?? 0,
        userReviews: userData.userReviews || [],
        authUserReviews: userData.authUserReviews || [],
        qrCode: userData.qrCode,
        unseenNotificationCount: userData.unseenNotificationCount ?? 0,
        deepLinkUrl: userData.deepLinkUrl,
        appSetting: userData.appSetting,
        photos: photosList,
        photosMeta: !Array.isArray(photosPayload) ? photosPayload : null,
        missingFields,
        welcomeBack: envelope.welcomeBack ?? null,
        onboarding,
        preferences: mergedPreferences,
    };
}

/** User stub from friends/search lists → fields FriendListItem and UserAvatar render. */
export function normalizeListedUser(raw: any): any {
    if (!raw || typeof raw !== 'object') return raw;
    const profile = raw.profile && typeof raw.profile === 'object' && !Array.isArray(raw.profile)
        ? raw.profile
        : {};
    const name = profile.name || raw.name || raw.userName || raw.user_name || raw.slug || '';
    const avatar = raw.avatar
        || raw.profilePhoto
        || raw.profile_photo
        || raw.avatarUrl
        || raw.profilePhotoPath
        || raw.profile_photo_path
        || null;

    return {
        ...raw,
        id: Number(raw.id),
        name,
        avatar,
        avatar_thumbnail: raw.avatar_thumbnail || raw.avatarThumbnail || avatar,
        subscribed: Boolean(raw.isPremium ?? raw.is_premium ?? raw.subscribed),
        id_verified: Boolean(
            raw.isVerified
            ?? raw.id_verified
            ?? raw.idVerified
            ?? raw.verified
        ),
        ambassador: Boolean(raw.ambassador ?? raw.isAmbassador),
        account_type: raw.account_type || raw.accountType,
        relationship_status: raw.relationship_status || raw.relationshipStatus || raw.accountType,
    };
}

/**
 * GET /friends/requests rows are the friendship record. The person is `sender`.
 * Returned `id` is the sender; `requestId` is the row id used by accept/reject.
 */
export function normalizeFriendRequest(item: any): any {
    if (!item || typeof item !== 'object') return item;
    const peer = item.sender
        ?? item.friendUser
        ?? item.friend
        ?? item.requester
        ?? item.user
        ?? item.profile
        ?? item.person
        ?? {};
    const listed = normalizeListedUser({
        ...peer,
        id: peer.id ?? item.userId ?? item.user_id,
    });
    return {
        ...listed,
        requestId: Number(item.requestId ?? item.request_id ?? item.id),
        befriended_at: item.acceptedAt ?? item.accepted_at ?? null,
    };
}

/** Extract paginated list from ResponseWrapper.data = { data, pagination }. */
export function normalizePaginated(payload: any): {data: any[]; pagination: any} {
    if (!payload) return {data: [], pagination: {page: 1, limit: 20, total: 0, totalPages: 0}};
    if (Array.isArray(payload)) return {data: payload, pagination: {page: 1, limit: payload.length, total: payload.length, totalPages: 1}};
    if (Array.isArray(payload.data)) {
        return {
            data: payload.data,
            pagination: payload.pagination || {page: 1, limit: payload.data.length, total: payload.data.length, totalPages: 1},
        };
    }
    return {data: [], pagination: payload.pagination || {page: 1, limit: 20, total: 0, totalPages: 0}};
}

/** dataURL / bare base64 → Blob for multipart uploads. */
export async function dataUrlToBlob(dataUrl: string, fallbackType = 'image/jpeg'): Promise<Blob> {
    if (dataUrl.startsWith('blob:') || dataUrl.startsWith('http')) {
        const res = await fetch(dataUrl);
        return res.blob();
    }
    const match = dataUrl.match(/^data:([^;]+);base64,(.*)$/);
    const mime = match?.[1] || fallbackType;
    const b64 = match?.[2] || dataUrl;
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], {type: mime});
}
